import axios from "axios";
import { getUniqueCashboxForMovement } from "./cashbox";
import { sessionRequestConfig } from "./csrf";

const API_URL = process.env.REACT_APP_API_URL;

/* general helpers */
export const formatNumber = (num) => {
  const number = Number(num);
  if (isNaN(number)) {
    console.warn("Valor inválido para formatNumber:", num);
    return "0,00";
  }
  const [integer, decimal] = number.toFixed(2).split(".");
  const integerFormatted = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${integerFormatted},${decimal}`;
};

/* wrappers around axios for consistent base url */
const _get = (path, config) => axios.get(`${API_URL}/${path}`, config);
const _post = (path, data) => axios.post(`${API_URL}/${path}`, data);
const _put = (path, data, config) => axios.put(`${API_URL}/${path}`, data, config);
const _delete = (path) => axios.delete(`${API_URL}/${path}`);
const hasValidId = (id) => id !== null && id !== undefined && id !== '' && typeof id !== 'object';

export const fetchCajas = () => _get(`cajas/`);
export const fetchMovimientos = (params) => _get(`movimiento/`, { params });
export const fetchMovimiento = (id) => {
  if (!hasValidId(id)) {
    return Promise.reject(new Error("Movimiento no encontrado: id invalido."));
  }
  return _get(`movimiento/${id}/`);
};
export const deleteMovimiento = (id) => _delete(`movimiento/${id}/`);
export const updateCaja = (id, data) => _put(`cajas/${id}/`, data);

export const fetchReportesDiarios = () => _get(`reporte_diario/`);
export const updateReporteDiario = (id, payload) => _put(`reporte_diario/${id}/`, payload);

export const deleteEstadia = (id) => _delete(`estadias/${id}/`);
export const updateEstadia = (id, payload) => _put(`estadias/${id}/`, payload);

export const fetchTickets = (tipo, params) => _get(`tickets-${tipo}/`, { params });
export const createTicket = (tipo, payload) => _post(`tickets-${tipo}/`, payload);
export const updateTicket = (tipo, id, payload) => _put(`tickets-${tipo}/${id}/`, payload);
export const fetchTicketPdf = (tipo, id) => axios.get(`${API_URL}/tickets-${tipo}/${id}/`, { responseType: 'blob' });

/* utilities that encapsulate multiple requests */
export async function handleDeleteMovimiento( id ) {
  if (!hasValidId(id)) return;
  const movimientosResp = await fetchMovimiento(id);
  const movimientoAEliminar = movimientosResp.data;
  if (!movimientoAEliminar) return;

  const caja = await getUniqueCashboxForMovement(API_URL, id);

  // update totals
  let total_ingresos = Number(caja.total_ingresos);
  let total_egresos = Number(caja.total_egresos);
  const tipo = movimientoAEliminar.tipo.toLowerCase();
  const monto = Number(parseFloat(movimientoAEliminar.monto));
  if (tipo === "ingreso") total_ingresos -= monto;
  else if (tipo === "egreso") total_egresos -= monto;

  const cajaSinMovimiento = caja.movimientos.filter(c => c !== id);

  await updateCaja(caja.id, {
    ...caja,
    movimientos: cajaSinMovimiento,
    total_ingresos: Number(total_ingresos.toFixed(2)),
    total_egresos: Number(total_egresos.toFixed(2)),
  });

  await deleteMovimiento(id);
}

export async function actualizarReporteDiarioValor(valorEliminado, fecha) {
  try {
    const response = await fetchReportesDiarios();
    const reportes = response.data;
    const reporteExistente = reportes.find(r => r.fecha === fecha && r.tipo === "E-I");
    if (reporteExistente) {
      const nuevoValor = parseFloat(reporteExistente.valor) - parseFloat(valorEliminado);
      await updateReporteDiario(reporteExistente.id, { valor: nuevoValor });
    }
  } catch (error) {
    console.error("Error en la actualización del reporte:", error);
  }
}

export async function actualizarReporteDiarioBorrar(fecha) {
  try {
    const response = await fetchReportesDiarios();
    const reportes = response.data;
    const reporteExistente = reportes.find(r => r.fecha === fecha && r.tipo === "E-V");
    if (reporteExistente) {
      await updateReporteDiario(reporteExistente.id, { valor: Number(reporteExistente.valor) - 1 });
    }
  } catch (error) {
    console.error("Error en la actualización del reporte:", error);
  }
}

/* helper to reserve a user-initiated popup before async PDF work starts */
export function openPdfPopup() {
  const opened = window.open("about:blank", "_blank");
  if (!opened) {
    window.alert("No se pudo abrir el PDF. Habilita las ventanas emergentes para imprimir el ticket.");
    return null;
  }
  opened.opener = null;
  return opened;
}

export function closePdfPopup(opened) {
  if (opened && !opened.closed) {
    opened.close();
  }
}

/* helper to open pdf blob in new window */
export function openPdfBlob(blob, opened = null) {
  const url = window.URL.createObjectURL(blob);
  if (opened && !opened.closed) {
    opened.location.href = url;
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    return;
  }

  const fallback = window.open(url, "_blank", "noopener,noreferrer");
  if (!fallback) {
    window.URL.revokeObjectURL(url);
    window.alert("No se pudo abrir el PDF. Habilita las ventanas emergentes para imprimir el ticket.");
    return;
  }
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

/**
 * Update a movimiento's monto and propagate the change to the containing caja totals.
 * Returns updated movimiento response.
 */
export async function updateMovimientoMonto(movimientoId, newMonto) {
  if (!hasValidId(movimientoId)) {
    throw new Error("No se puede actualizar el movimiento porque no hay un id valido.");
  }
  // fetch the movimiento
  const movimientosResp = await fetchMovimiento(movimientoId);
  const movimiento = movimientosResp.data;
  if (!movimiento) throw new Error("Movimiento no encontrado");

  const caja = await getUniqueCashboxForMovement(API_URL, movimiento.id);
  if (!caja) throw new Error("No se encontró la caja del movimiento.");
  const response = await _put(
    `movimiento/${movimiento.id}/`,
    {
      caja_id: caja.id,
      movimiento: {
        monto: newMonto,
        isME: 'estadia',
        tipo: movimiento.tipo,
      },
    },
    sessionRequestConfig()
  );

  return response.data;
}
